import { Change, NodeId } from '@fluid-experimental/tree';
import { Serializable } from '@fluidframework/datastore-definitions';
import { DestinationPosition, SourcePosition } from '../../../store/mfsTypes';
import { COLLECTION_VALUES } from '../types';

export const ELEMENTS = COLLECTION_VALUES;

/** The stable unique identifier of the element in the OrderedList. */
export type ElementId = NodeId;

/** An element of the OrderedList. */
export type OrderedListElement<T> = {
  elementValue: Serializable<T>;
  /** The unique id of the element. */
  elementId: ElementId;
  /** The id of the parent node for the element. Only included for elements at deeper than level-1. */
  parentId: ElementId;
};

export type OrderedListWriterType<T> = {
  /** Inserts the items to the specificied position */
  insert(position: DestinationPosition, items: Serializable<T>[]): ChangesAndIds<T>;

  /**
   * Moves the item `from` position in current collection `to` position (optionally in a different collection under
   * `destinationRootId`).
   */
  move(from: SourcePosition, to: DestinationPosition, destinationRoot?: NodeId): Change[];

  /**
   * Removes the item at `position`.
   *
   * @returns Undefined, if the item was already removed; the remove change otherwise.
   */
  remove(position: ElementId): Change | undefined;

  /**
   * Returns changes to remove all or a sub-tree of items from the current snapshot.
   *
   * @param parentId If specified, removes only items that are parented under parentId; otherwise removes all items.
   */
  clear(parentId?: ElementId): Change[];
};

export type OrderedListReaderType<T> = {
  /** Returns the item at `position`. */
  get(position: ElementId): Serializable<T>;

  /**
   * Gets an iterator over all or a sub-tree of the elements.
   *
   * @param parentId If specified, yields only elements that are parented under parentId.
   * @yields The list element.
   */
  getElements(parentId?: ElementId, isReverse?: boolean): IterableIterator<OrderedListElement<T>>;

  /** Whether the elementId is an element of the current collection. */
  hasElement(elementId: ElementId): boolean;
};

export type OrderedListEditorType<T> = OrderedListReaderType<T> & OrderedListWriterType<T>;

export type ChangesAndIds<T> = {
  changes: Change[];
  itemNodeIds: [Serializable<T>, NodeId][];
};
